// src/pages/ReceiveDonation.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../components/Header";
import ThaiBEDateRangePicker from "../components/ThaiBEDateRangePicker";
import { ReactComponent as CalendarIcon } from "../assets/images/calendar.svg";
import { ReactComponent as Trash } from "../assets/images/trash.svg";
import { createHttpClient } from "../api/client";

function uid(prefix = "id") {
    return `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now()}`;
}

const OTHER_BRANCH_VALUE = "__other__";
const OTHER_FOOD_NAME = "อื่นๆ"; // สำหรับ dropdown ประเภทอาหาร

function pad2(n) {
    return String(n).padStart(2, "0");
}

function toYMD(date) {
    if (!date) return "";
    const d = new Date(date);
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function safeNum(x) {
    const n = Number(x);
    return Number.isFinite(n) ? n : 0;
}

function readStoredProfile() {
    const keys = ["profile", "PROFILE", "user_profile", "USER_PROFILE"];
    for (const k of keys) {
        const raw = localStorage.getItem(k);
        if (!raw) continue;
        try {
            const obj = JSON.parse(raw);
            if (obj?.user) return obj;
        } catch { }
    }
    return null;
}

function SuccessIcon() {
    return (
        <div className="h-14 w-14 rounded-full bg-emerald-600 grid place-items-center">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
                <path
                    d="M20 6L9 17l-5-5"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </div>
    );
}

export default function ReceiveDonation() {
    const nav = useNavigate();
    const location = useLocation();
    const http = useMemo(() => createHttpClient(), []);
    const today = useMemo(() => new Date(), []);

    const [step, setStep] = useState(1);
    const [range, setRange] = useState({ startDate: today, endDate: today });

    // profile
    const [profileLoading, setProfileLoading] = useState(true);
    const [profileError, setProfileError] = useState("");
    const [donorId, setDonorId] = useState(null);
    const [hubId, setHubId] = useState(null);

    // food types
    const [foodTypeLoading, setFoodTypeLoading] = useState(true);
    const [foodTypeError, setFoodTypeError] = useState("");
    const [foodTypes, setFoodTypes] = useState([]); // [{id,name,is_active,is_delete}]

    // donation type by hub
    const [donationTypeId, setDonationTypeId] = useState("");
    const [donationTypeLoading, setDonationTypeLoading] = useState(false);
    const [donationTypeError, setDonationTypeError] = useState("");
    const [donationTypes, setDonationTypes] = useState([]); // [{id,name,...}]

    // branches
    const [branchLoading, setBranchLoading] = useState(false);
    const [branchError, setBranchError] = useState("");
    const [branches, setBranches] = useState([]); // [{id,name}]
    const [branchId, setBranchId] = useState("");
    const [branchDetail, setBranchDetail] = useState("");

    // items (step2)
    // ✅ เพิ่ม foodName สำหรับกรณีเลือก "อื่นๆ"
    const [items, setItems] = useState([]); // [{id, foodTypeId, foodName, qty}]
    const totalItems = items.length;

    // templates (drafts) via API
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [templatesError, setTemplatesError] = useState("");
    const [templates, setTemplates] = useState([]); // [{template_id, template_name, template_created_at, items, total_weight}]

    // save template modal
    const [saveTplOpen, setSaveTplOpen] = useState(false);
    const [tplName, setTplName] = useState("");
    const [tplSaving, setTplSaving] = useState(false);
    const [tplSaveError, setTplSaveError] = useState("");

    // delete template modal
    const [deleteTplOpen, setDeleteTplOpen] = useState(false);
    const [pendingDeleteTpl, setPendingDeleteTpl] = useState(null);
    const [tplDeleting, setTplDeleting] = useState(false);

    // submit donation
    const [loading, setLoading] = useState(false);
    const [successOpen, setSuccessOpen] = useState(false);

    // --- read query params for date range (1 day) ---
    useEffect(() => {
        const sp = new URLSearchParams(location.search);
        const start = sp.get("start_date") || sp.get("startdate");
        const end = sp.get("end_date") || sp.get("enddate");

        const sd = start ? new Date(start) : null;
        const ed = end ? new Date(end) : null;

        if (sd && !Number.isNaN(sd.getTime())) {
            setRange({ startDate: sd, endDate: sd });
        } else if (ed && !Number.isNaN(ed.getTime())) {
            setRange({ startDate: ed, endDate: ed });
        }
    }, [location.search]);

    // --- load profile ---
    useEffect(() => {
        let alive = true;

        (async () => {
            setProfileLoading(true);
            setProfileError("");

            try {
                const res = await http.get("/api/auth/profile");
                const user = res?.data?.user;

                const id = user?.id;
                const hub = user?.addresses?.[0]?.hub_id;

                if (!id) throw new Error("missing user.id");

                if (!alive) return;

                setDonorId(Number(id));
                setHubId(hub ? Number(hub) : null);
                localStorage.setItem("profile", JSON.stringify(res.data));
            } catch (e) {
                console.error("GET /api/auth/profile error:", e);

                const cached = readStoredProfile();
                const id = cached?.user?.id;
                const hub = cached?.user?.addresses?.[0]?.hub_id;

                if (!alive) return;

                if (id) {
                    setDonorId(Number(id));
                    setHubId(hub ? Number(hub) : null);
                    setProfileError("");
                } else {
                    setProfileError("โหลดข้อมูลผู้ใช้งานไม่สำเร็จ");
                    setDonorId(null);
                    setHubId(null);
                }
            } finally {
                if (alive) setProfileLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [http]);

    // --- load branches by hub + donationType ---
    useEffect(() => {
        let alive = true;

        (async () => {
            // reset ทุกครั้งที่ hub / donationType เปลี่ยน
            setBranchError("");
            setBranches([]);
            setBranchId("");
            setBranchDetail("");

            // ยังไม่พร้อม -> ไม่ต้องโหลด
            if (!hubId || !donationTypeId) {
                if (alive) setBranchLoading(false);
                return;
            }

            setBranchLoading(true);
            try {
                const res = await http.get("/api/branch/getBranchByDonationType", {
                    params: {
                        hub_id: hubId,                 // จาก profile/localStorage (state hubId)
                        donation_type_id: donationTypeId, // id จากประเภทผู้บริจาค
                    },
                });

                const list = Array.isArray(res?.data?.data) ? res.data.data : [];
                if (!alive) return;
                setBranches(list);
            } catch (e) {
                console.error("GET /api/branch/getBranchByDonationType error:", e);
                if (!alive) return;
                setBranchError("โหลดรายการสถานที่เข้ารับไม่สำเร็จ");
                setBranches([]);
            } finally {
                if (alive) setBranchLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [http, hubId, donationTypeId]);

    // --- load food types ---
    useEffect(() => {
        let alive = true;

        (async () => {
            setFoodTypeLoading(true);
            setFoodTypeError("");

            try {
                const res = await http.get("/api/foodtype?page=1&limit=100");
                const list = Array.isArray(res?.data?.data) ? res.data.data : [];

                let usable = list.filter((x) => x?.is_active && !x?.is_delete);

                // กันไม่มี "อื่นๆ" ในระบบ
                const hasOther = usable.some((x) => String(x?.name || "").trim() === OTHER_FOOD_NAME);
                if (!hasOther) {
                    usable = [...usable, { id: 16, name: OTHER_FOOD_NAME, is_active: true, is_delete: false }];
                }

                // unique by id
                const seen = new Set();
                usable = usable.filter((x) => {
                    const k = String(x.id);
                    if (seen.has(k)) return false;
                    seen.add(k);
                    return true;
                });

                if (!alive) return;
                setFoodTypes(usable);
            } catch (e) {
                console.error("GET /api/foodtype error:", e);
                if (!alive) return;
                setFoodTypeError("โหลดประเภทอาหารไม่สำเร็จ");
                setFoodTypes([]);
            } finally {
                if (alive) setFoodTypeLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [http]);

    // --- load donation types by hub ---
    useEffect(() => {
        let alive = true;

        (async () => {
            setDonationTypeId("");
            setDonationTypes([]);
            setDonationTypeError("");

            if (!hubId) return;

            setDonationTypeLoading(true);
            try {
                const res = await http.get(`/api/location/donationTypes/${hubId}`);
                const list = Array.isArray(res?.data?.data) ? res.data.data : [];
                if (!alive) return;
                setDonationTypes(list);
            } catch (e) {
                console.error("GET /api/location/donationTypes/:hubId error:", e);
                if (!alive) return;
                setDonationTypeError("โหลดประเภทผู้บริจาคไม่สำเร็จ");
                setDonationTypes([]);
            } finally {
                if (alive) setDonationTypeLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [http, hubId]);

    // --- load templates by donorId ---
    async function fetchTemplates(userId) {
        if (!userId) return;
        setTemplatesLoading(true);
        setTemplatesError("");
        try {
            const res = await http.get(`/api/templates/getTemplates/${userId}`);
            const list = Array.isArray(res?.data?.data) ? res.data.data : [];
            setTemplates(list);
        } catch (e) {
            console.error("GET /api/templates/getTemplates/:id error:", e);
            setTemplatesError("โหลดแบบร่างไม่สำเร็จ");
            setTemplates([]);
        } finally {
            setTemplatesLoading(false);
        }
    }

    useEffect(() => {
        let alive = true;
        (async () => {
            if (!donorId) return;
            await fetchTemplates(donorId);
            if (!alive) return;
        })();
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [donorId]);

    // --- helpers ---
    function getFoodNameById(id) {
        const hit = foodTypes.find((x) => String(x.id) === String(id));
        return hit?.name ? String(hit.name).trim() : "";
    }

    const OTHER_FOOD_TYPE_ID = useMemo(() => {
        const hit = foodTypes.find((x) => String(x?.name || "").trim() === OTHER_FOOD_NAME);
        return hit?.id ? String(hit.id) : "16";
    }, [foodTypes]);

    function addEmptyItem() {
        setItems((prev) => [...prev, { id: uid("row"), foodTypeId: "", foodName: "", qty: "" }]);
    }

    function updateItem(id, patch) {
        setItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    }

    function deleteItem(id) {
        setItems((prev) => prev.filter((x) => x.id !== id));
    }

    // ✅ items สำหรับ POST /api/donations/addDonation (คง logic เดิม + รองรับ foodName ตอน "อื่นๆ")
    function buildDonationItems() {
        return items
            .map((x) => {
                const foodTypeIdNum = Number(x.foodTypeId);
                const weight = safeNum(x.qty);

                if (!Number.isFinite(foodTypeIdNum) || foodTypeIdNum <= 0) return null;
                if (!Number.isFinite(weight) || weight <= 0) return null;

                const typeName = getFoodNameById(foodTypeIdNum);

                if (typeName === OTHER_FOOD_NAME) {
                    return {
                        food_type_id: foodTypeIdNum, // ใช้ id ของ "อื่นๆ"
                        food_name: (x.foodName || "").trim() || OTHER_FOOD_NAME,
                        weight,
                    };
                }

                return {
                    food_type_id: foodTypeIdNum,
                    food_name: null,
                    weight,
                };
            })
            .filter(Boolean);
    }

    // ✅ items สำหรับ POST /api/templates/addTemplates (ตามตัวอย่าง: "อื่นๆ" -> food_type_id null และใช้ food_name)
    function buildTemplateItems() {
        return items
            .map((x) => {
                const foodTypeIdNum = Number(x.foodTypeId);
                const weight = safeNum(x.qty);

                if (!Number.isFinite(weight) || weight <= 0) return null;

                // ต้องเลือกประเภทก่อน
                if (!Number.isFinite(foodTypeIdNum) || foodTypeIdNum <= 0) return null;

                const typeName = getFoodNameById(foodTypeIdNum);

                if (typeName === OTHER_FOOD_NAME) {
                    const name = (x.foodName || "").trim();
                    if (!name) return null; // template ต้องมีชื่ออาหารเมื่อเป็นอื่นๆ
                    return {
                        food_type_id: null,
                        food_name: name,
                        weight,
                    };
                }

                return {
                    food_type_id: foodTypeIdNum,
                    food_name: null,
                    weight,
                };
            })
            .filter(Boolean);
    }

    // --- step validations ---
    const canGoNext =
        !!range.startDate &&
        !!donationTypeId &&
        !!branchId &&
        !branchLoading &&
        !branchError &&
        (branchId !== OTHER_BRANCH_VALUE || branchDetail.trim().length > 0);

    const donationItems = useMemo(() => buildDonationItems(), [items, foodTypes]); // eslint-disable-line react-hooks/exhaustive-deps
    const canSubmit =
        !profileLoading && !profileError && !!donorId && !!hubId && donationItems.length > 0;

    // ✅ ปุ่มบันทึกแบบร่าง: เปิดได้เมื่อมี item ที่พอใช้ได้ (เงื่อนไข template)
    const canOpenSaveTemplate = useMemo(() => {
        const tplItems = buildTemplateItems();
        return !profileLoading && !profileError && !!donorId && tplItems.length > 0;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, foodTypes, donorId, profileLoading, profileError]);

    function goBack() {
        if (step === 1) nav(-1);
        else setStep(1);
    }

    function handleNext() {
        if (!canGoNext) return;
        setStep(2);
    }

    // --- templates actions ---
    function applyTemplate(tpl) {
        const arr = Array.isArray(tpl?.items) ? tpl.items : [];
        if (arr.length === 0) return;

        const mapped = arr.map((it) => {
            const isOther = it?.food_type_id == null && (it?.food_name || "").trim();
            return {
                id: uid("row"),
                foodTypeId: isOther ? String(OTHER_FOOD_TYPE_ID) : String(it?.food_type_id ?? ""),
                foodName: isOther ? String(it.food_name || "").trim() : "",
                qty: "", // ✅ เริ่มต้นเป็นค่าว่าง
            };
        });

        setItems(mapped);
    }

    function openSaveTemplateModal() {
        if (!canOpenSaveTemplate) return;
        setTplName("");
        setTplSaveError("");
        setSaveTplOpen(true);
    }

    async function confirmSaveTemplate() {
        if (tplSaving) return;

        const name = String(tplName || "").trim();
        if (!name) {
            setTplSaveError("กรุณากรอกชื่อแบบร่าง");
            return;
        }

        const tplItems = buildTemplateItems();
        if (tplItems.length === 0) {
            setTplSaveError("กรุณาเพิ่มรายการให้ครบถ้วนก่อนบันทึกแบบร่าง");
            return;
        }

        if (!donorId) {
            setTplSaveError("ไม่พบผู้ใช้งาน");
            return;
        }

        try {
            setTplSaving(true);
            setTplSaveError("");

            const payload = {
                donor_id: Number(donorId),
                name,
                items: tplItems,
            };

            await http.post("/api/templates/addTemplates", payload);

            setSaveTplOpen(false);
            setTplName("");

            // refresh templates
            await fetchTemplates(donorId);
        } catch (e) {
            console.error("POST /api/templates/addTemplates error:", e);
            setTplSaveError("บันทึกแบบร่างไม่สำเร็จ กรุณาลองใหม่");
        } finally {
            setTplSaving(false);
        }
    }

    function askDeleteTemplate(tpl) {
        setPendingDeleteTpl(tpl);
        setDeleteTplOpen(true);
    }

    async function confirmDeleteTemplate() {
        if (!pendingDeleteTpl || tplDeleting) return;

        try {
            setTplDeleting(true);

            // ⚠️ backend ของคุณระบุ path เป็น /api/templates/delete/{donor_id}
            // แต่ response มี template_id -> ปกติจะลบด้วย template_id
            // โค้ดนี้จะส่ง template_id ไปตาม path
            const templateId = pendingDeleteTpl?.template_id;
            await http.delete(`/api/templates/delete/${templateId}`);

            setDeleteTplOpen(false);
            setPendingDeleteTpl(null);

            await fetchTemplates(donorId);
        } catch (e) {
            console.error("DELETE /api/templates/delete/:id error:", e);
            alert("ลบแบบร่างไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
        } finally {
            setTplDeleting(false);
        }
    }

    // --- submit donation ---
    async function confirmSubmit() {
        if (!canSubmit) return;

        try {
            setLoading(true);

            const payload = {
                donor_id: Number(donorId),
                receive_by: null,
                donation_date: range.startDate ? toYMD(range.startDate) : null,
                receive_date: range.startDate ? toYMD(range.startDate) : null,
                donation_type_id: Number(donationTypeId),
                hub_id: Number(hubId),
                branch_id: branchId === OTHER_BRANCH_VALUE ? null : Number(branchId),
                branch_name_detail: branchId === OTHER_BRANCH_VALUE ? branchDetail.trim() : null,
                status: "draft",
                use_latest_template: false,
                items: donationItems,
            };

            await http.post("/api/donations/addDonation", payload);

            setSuccessOpen(true);
            setTimeout(() => {
                setSuccessOpen(false);
                nav("/donation_list");
            }, 900);
        } catch (e) {
            console.error("POST /api/donations/addDonation error:", e);
            alert("บันทึกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
        } finally {
            setLoading(false);
        }
    }

    const overlayLoading =
        loading || profileLoading || foodTypeLoading || donationTypeLoading || branchLoading || templatesLoading || tplSaving || tplDeleting;

    return (
        <div className="min-h-screen bg-white flex flex-col pb-32 sm:w-full md:max-w-[520px] items-center self-center mx-auto">
            <div className="w-full">
                <Header />
            </div>

            {/* Loading overlay */}
            {overlayLoading && (
                <div className="fixed inset-0 z-[999] bg-white/70 backdrop-blur-[2px] grid place-items-center">
                    <div className="flex items-center gap-3 rounded-2xl bg-white border border-slate-200 px-5 py-4 shadow">
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
                        <div className="text-[14px] text-[#4B5563]">กำลังโหลดข้อมูล…</div>
                    </div>
                </div>
            )}

            {/* Header + Stepper */}
            <div className="pb-4 w-full px-4 items-start flex flex-col">
                <button
                    type="button"
                    onClick={goBack}
                    aria-label="ย้อนกลับ"
                    className="flex items-center gap-2 w-full self-start justify-start text-[#4B5563]"
                >
                    <div className="grid place-items-center rounded-full hover:bg-slate-100">
                        <span className="text-2xl leading-none">‹</span>
                    </div>
                    <div className="text-[18px] font-bold">รับอาหารบริจาค</div>
                </button>

                <Stepper step={step} />
            </div>

            {/* Errors */}
            {(profileError || donationTypeError || branchError || foodTypeError || templatesError) && (
                <div className="w-full px-4 space-y-2">
                    {profileError && (
                        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                            {profileError}
                        </div>
                    )}
                    {donationTypeError && (
                        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                            {donationTypeError}
                        </div>
                    )}
                    {branchError && (
                        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                            {branchError}
                        </div>
                    )}
                    {foodTypeError && (
                        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                            {foodTypeError}
                        </div>
                    )}
                    {templatesError && (
                        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                            {templatesError}
                        </div>
                    )}
                </div>
            )}

            {/* Delete template modal */}
            <Modal open={deleteTplOpen}>
                <div className="w-[320px] rounded-3xl bg-white p-6 shadow-xl border border-slate-100 text-center">
                    <div className="text-[18px] font-bold text-slate-900">ลบแบบร่างนี้?</div>
                    <div className="mt-2 text-[13px] text-slate-500">
                        {pendingDeleteTpl ? `“${pendingDeleteTpl.template_name}”` : ""}
                    </div>

                    <div className="mt-5 flex gap-3">
                        <button
                            type="button"
                            onClick={() => {
                                if (tplDeleting) return;
                                setDeleteTplOpen(false);
                                setPendingDeleteTpl(null);
                            }}
                            className="w-1/2 h-11 rounded-xl border-2 border-slate-300 bg-white font-bold text-[#4B5563]"
                        >
                            ยกเลิก
                        </button>
                        <button
                            type="button"
                            onClick={confirmDeleteTemplate}
                            disabled={tplDeleting}
                            className={[
                                "w-1/2 h-11 rounded-xl font-bold text-white",
                                tplDeleting ? "bg-slate-300 cursor-not-allowed" : "bg-red-500",
                            ].join(" ")}
                        >
                            ลบ
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Save template modal */}
            <Modal open={saveTplOpen}>
                <div className="w-[340px] rounded-3xl bg-white p-6 shadow-xl border border-slate-100">
                    <div className="text-center">
                        <div className="text-[18px] font-bold text-slate-900">บันทึกแบบร่าง</div>
                        <div className="mt-1 text-[13px] text-slate-500">กรอกชื่อแบบร่างก่อนบันทึก</div>
                    </div>

                    <div className="mt-5">
                        <div className="text-[13px] font-semibold text-[#4B5563]">
                            ชื่อแบบร่าง <span className="text-red-500">*</span>
                        </div>
                        <input
                            className="mt-2 w-full rounded-xl border-2 border-slate-300 bg-white px-4 py-3 text-[14px] outline-none"
                            placeholder="เช่น ของบริจาคประจำเดือน"
                            value={tplName}
                            onChange={(e) => setTplName(e.target.value)}
                            disabled={tplSaving}
                        />
                        {tplSaveError && <div className="mt-2 text-[12px] text-rose-600">{tplSaveError}</div>}
                    </div>

                    <div className="mt-6 flex gap-3">
                        <button
                            type="button"
                            onClick={() => {
                                if (tplSaving) return;
                                setSaveTplOpen(false);
                                setTplName("");
                                setTplSaveError("");
                            }}
                            className="w-1/2 h-11 rounded-xl border-2 border-slate-300 bg-white font-bold text-[#4B5563]"
                        >
                            ยกเลิก
                        </button>
                        <button
                            type="button"
                            onClick={confirmSaveTemplate}
                            disabled={tplSaving}
                            className={[
                                "w-1/2 h-11 rounded-xl font-bold text-white",
                                tplSaving ? "bg-slate-300 cursor-not-allowed" : "bg-[#3E6F5E]",
                            ].join(" ")}
                        >
                            บันทึก
                        </button>
                    </div>
                </div>
            </Modal>

            {/* STEP 1 */}
            {step === 1 ? (
                <div className="pt-4 space-y-4 w-full px-4 items-center">
                    <Field label="วันที่เข้ารับ" required>
                        <div className="w-fit rounded-xl bg-white border-2 border-slate-300 px-3 py-2 shadow-sm">
                            <ThaiBEDateRangePicker
                                modeKey="1day"
                                today={today}
                                Icon={CalendarIcon}
                                startDate={range.startDate}
                                endDate={range.endDate}
                                onChange={({ startDate, endDate }) => {
                                    const d = startDate || endDate || new Date();
                                    setRange({ startDate: d, endDate: d });
                                }}
                            />
                        </div>
                    </Field>

                    <Field label="ประเภทผู้บริจาค" required>
                        <select
                            className="w-full max-w-[520px] rounded-xl border-2 border-slate-300 bg-white px-4 py-3 text-[14px] outline-none"
                            value={donationTypeId}
                            onChange={(e) => {
                                setDonationTypeId(e.target.value);
                                setBranchId("");
                                setBranchDetail("");
                            }}
                            disabled={
                                profileLoading ||
                                !!profileError ||
                                !hubId ||
                                donationTypeLoading ||
                                !!donationTypeError
                            }
                        >
                            <option value="" disabled>
                                {!hubId
                                    ? "ไม่พบ hub ของผู้ใช้"
                                    : donationTypeLoading
                                        ? "กำลังโหลดประเภทผู้บริจาค..."
                                        : donationTypeError
                                            ? "โหลดประเภทผู้บริจาคไม่สำเร็จ"
                                            : "เลือกประเภทผู้บริจาค"}
                            </option>

                            {donationTypes.map((x) => (
                                <option key={x.id} value={String(x.id)}>
                                    {x.name}
                                </option>
                            ))}
                        </select>
                    </Field>

                    <Field label="สถานที่เข้ารับ" required>
                        <div className="space-y-2 max-w-[520px]">
                            <select
                                className="w-full rounded-xl border-2 border-slate-300 bg-white px-4 py-3 text-[14px] outline-none"
                                value={branchId}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setBranchId(v);
                                    if (v !== OTHER_BRANCH_VALUE) setBranchDetail("");
                                }}
                                disabled={!hubId || !donationTypeId || branchLoading || !!branchError}
                            >
                                <option value="" disabled>
                                    {!hubId
                                        ? "ไม่พบ hub ของผู้ใช้"
                                        : !donationTypeId
                                            ? "กรุณาเลือกประเภทผู้บริจาคก่อน"
                                            : branchLoading
                                                ? "กำลังโหลดสถานที่เข้ารับ..."
                                                : branchError
                                                    ? "โหลดสถานที่เข้ารับไม่สำเร็จ"
                                                    : "เลือกสถานที่เข้ารับ"}
                                </option>

                                {branches.map((b) => (
                                    <option key={b.id} value={String(b.id)}>
                                        {b.name}
                                    </option>
                                ))}

                                <option value={OTHER_BRANCH_VALUE}>อื่นๆ</option>
                            </select>

                            {branchId === OTHER_BRANCH_VALUE && (
                                <input
                                    className="w-full rounded-xl border-2 border-slate-300 bg-white px-4 py-3 text-[14px] outline-none"
                                    placeholder="กรอกรายละเอียดสถานที่เข้ารับ"
                                    value={branchDetail}
                                    onChange={(e) => setBranchDetail(e.target.value)}
                                />
                            )}

                            {branchId === OTHER_BRANCH_VALUE && branchDetail.trim().length === 0 && (
                                <div className="text-[12px] text-rose-600">กรุณากรอกรายละเอียดสถานที่เข้ารับ</div>
                            )}
                        </div>
                    </Field>

                    <BottomFixed>
                        <button
                            type="button"
                            onClick={handleNext}
                            disabled={!canGoNext}
                            className={[
                                "w-full max-w-[560px] rounded-xl py-3 text-[16px] font-bold",
                                canGoNext ? "bg-[#3E6F5E] text-white" : "bg-slate-200 text-slate-500 cursor-not-allowed",
                            ].join(" ")}
                        >
                            ถัดไป
                        </button>
                    </BottomFixed>
                </div>
            ) : (
                /* STEP 2 */
                <div className="pt-4 pb-24 w-full px-4">
                    {/* Templates */}
                    <div className="mt-2 bg-[#F5F5F5] px-4 py-4 rounded-2xl">
                        <div className="flex items-center justify-between">
                            <div className="text-[20px] font-medium text-slate-900">Templates (แบบร่าง)</div>
                            <button
                                type="button"
                                onClick={() => fetchTemplates(donorId)}
                                className="text-[13px] font-semibold text-[#407865] underline"
                                disabled={!donorId || templatesLoading}
                            >
                                รีเฟรช
                            </button>
                        </div>

                        <div className="mt-4 overflow-x-auto pb-2">
                            <div className="flex gap-4 pr-2">
                                {templates.length === 0 ? (
                                    <DraftEmptyCard loading={templatesLoading} />
                                ) : (
                                    templates.map((t, idx) => (
                                        <DraftCard
                                            key={t.template_id}
                                            title={t.template_name}
                                            count={Array.isArray(t.items) ? t.items.length : 0}
                                            isLatest={idx === 0}
                                            totalWeight={t.total_weight}
                                            onAdd={() => applyTemplate(t)}
                                            onDelete={() => askDeleteTemplate(t)}
                                        />
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Items */}
                    <div className="mt-6 flex items-center justify-between">
                        <div className="text-[16px] font-bold text-slate-900">รายการอาหารที่ต้องการบริจาค</div>

                        <button
                            type="button"
                            onClick={addEmptyItem}
                            className="rounded-xl bg-[#ED9C50] px-5 py-2 text-[14px] font-bold text-white shadow-sm active:scale-[0.99]"
                        >
                            เพิ่ม
                        </button>
                    </div>

                    <div className="mt-3 space-y-3 max-w-[720px]">
                        {items.length === 0 ? (
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 text-slate-500 text-[14px]">
                                ยังไม่มีรายการ กด “เพิ่ม” เพื่อเริ่มต้น
                            </div>
                        ) : (
                            items.map((row) => {
                                const typeName = row.foodTypeId ? getFoodNameById(row.foodTypeId) : "";
                                const isOther = typeName === OTHER_FOOD_NAME;

                                return (
                                    <div key={row.id} className="rounded-2xl border border-slate-200 bg-white p-3">
                                        <div className="grid grid-cols-12 gap-3 items-start">
                                            <div className="col-span-6">
                                                <select
                                                    className="w-full rounded-xl border-2 border-slate-300 bg-white px-4 py-3 text-[14px] outline-none"
                                                    value={row.foodTypeId}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        updateItem(row.id, { foodTypeId: v, foodName: v ? "" : "" });
                                                    }}
                                                    disabled={foodTypeLoading || !!foodTypeError}
                                                >
                                                    <option value="">
                                                        {foodTypeLoading ? "กำลังโหลดประเภทอาหาร..." : "ประเภทอาหาร"}
                                                    </option>

                                                    {foodTypes.map((x) => (
                                                        <option key={x.id} value={String(x.id)}>
                                                            {x.name}
                                                        </option>
                                                    ))}
                                                </select>

                                                {isOther && (
                                                    <input
                                                        className="w-full outline-none bg-transparent text-center pr-2"
                                                        type="number"
                                                        value={row.qty ?? ""}   // ✅ ให้ช่องว่างได้เสมอ
                                                        onChange={(e) => updateItem(row.id, { qty: e.target.value })}
                                                        inputMode="decimal"
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                )}

                                                {isOther && (row.foodName || "").trim().length === 0 && (
                                                    <div className="mt-1 text-[12px] text-rose-600">กรุณากรอกชื่ออาหารสำหรับ “อื่นๆ”</div>
                                                )}
                                            </div>

                                            <div className="col-span-4">
                                                <div className="w-full rounded-xl border-2 border-slate-300 bg-white px-4 py-3 text-[14px] flex items-center justify-between">
                                                    <input
                                                        className="w-full outline-none bg-transparent text-center pr-2"
                                                        type="number"
                                                        value={row.qty}
                                                        onChange={(e) => updateItem(row.id, { qty: e.target.value })}
                                                        inputMode="decimal"
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                    <span className="text-slate-400 whitespace-nowrap">กก.</span>
                                                </div>
                                            </div>

                                            <div className="col-span-2 flex justify-end">
                                                <button
                                                    type="button"
                                                    onClick={() => deleteItem(row.id)}
                                                    className="h-12 w-12 rounded-xl bg-[#F7E3E3] grid place-items-center hover:bg-red-100"
                                                    aria-label="ลบรายการ"
                                                >
                                                    <Trash className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    <div className="mt-4 max-w-[720px] flex items-center justify-between text-[14px]">
                        <div className="text-black">จำนวนรายการทั้งหมด:</div>
                        <div className="font-bold text-[#407865]">{totalItems} รายการ</div>
                    </div>

                    <BottomFixed>
                        <div className="w-full max-w-[720px] flex items-start justify-between gap-3">
                            <button
                                type="button"
                                onClick={() => setStep(1)}
                                className="w-1/2 h-12 rounded-xl border-2 border-slate-300 bg-white py-3 text-[16px] font-bold text-[#4B5563]"
                            >
                                ย้อนกลับ
                            </button>

                            <div className="w-1/2 flex flex-col gap-2">
                                <button
                                    type="button"
                                    onClick={confirmSubmit}
                                    disabled={!canSubmit}
                                    className={[
                                        "w-full h-12 rounded-xl py-3 text-[16px] font-bold text-white",
                                        canSubmit ? "bg-[#3E6F5E]" : "bg-slate-300 cursor-not-allowed",
                                    ].join(" ")}
                                >
                                    ยืนยัน
                                </button>

                                {/* ✅ ปรับสีปุ่มบันทึกแบบร่างให้ “เขียว” เมื่อกดได้ */}
                                <button
                                    type="button"
                                    onClick={openSaveTemplateModal}
                                    disabled={!canOpenSaveTemplate}
                                    className={[
                                        "w-full h-12 rounded-xl border-2 py-2 text-[14px] font-bold",
                                        canOpenSaveTemplate
                                            ? "border-[#69927F] bg-white text-[#407865] hover:bg-slate-50 active:scale-[0.99] transition"
                                            : "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed",
                                    ].join(" ")}
                                >
                                    บันทึกแบบร่าง
                                </button>
                            </div>
                        </div>
                    </BottomFixed>
                </div>
            )}

            {/* Success modal */}
            <Modal open={successOpen}>
                <div className="w-[320px] rounded-3xl bg-white p-6 shadow-xl border border-slate-100 text-center">
                    <div className="grid place-items-center">
                        <SuccessIcon />
                    </div>
                    <div className="mt-4 text-[18px] font-bold text-slate-900">บันทึกข้อมูลสำเร็จ</div>
                    <div className="mt-1 text-[13px] text-slate-500">ระบบกำลังพากลับไปหน้ารายการ</div>
                </div>
            </Modal>
        </div>
    );
}

/* ---------------- UI parts ---------------- */

function Stepper({ step }) {
    const s1Done = step >= 2;

    return (
        <div className="mt-3 flex items-center gap-3 text-[14px]">
            <div className="flex items-center gap-2">
                <Circle active={step === 1} done={s1Done} number="1" />
                <span className={s1Done ? "text-slate-600" : "text-emerald-700 font-semibold"}>
                    ข้อมูลผู้บริจาค
                </span>
            </div>

            <div className="h-[2px] w-10 bg-[#9CA3AF] rounded-full" />

            <div className="flex items-center gap-2">
                <Circle active={step === 2} done={false} number="2" />
                <span className={step === 2 ? "text-emerald-700 font-semibold" : "text-slate-600"}>
                    รายการอาหารบริจาค
                </span>
            </div>
        </div>
    );
}

function Circle({ active, done, number }) {
    if (done) {
        return <div className="h-7 w-7 rounded-full bg-[#447E45] text-white grid place-items-center">✓</div>;
    }
    return (
        <div
            className={[
                "h-7 w-7 rounded-full grid place-items-center border-2 font-bold",
                active ? "border-[#84DA8A] text-[#407865]" : "border-[#4B5563] text-[#9CA3AF]",
            ].join(" ")}
        >
            {number}
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <div>
            <div className="text-[14px] font-semibold text-[#4B5563]">
                {label} {required && <span className="text-red-500">*</span>}
            </div>
            <div className="mt-2">{children}</div>
        </div>
    );
}

function BottomFixed({ children }) {
    return (
        <div className="fixed bottom-0 left-0 right-0 z-[50] bg-white">
            <div className="px-4 sm:px-6 py-4 border-t border-[#9CA3AF] grid place-items-center rounded-t-3xl">
                {children}
            </div>
        </div>
    );
}

function Modal({ open, children }) {
    if (!open) return null;
    return <div className="fixed inset-0 z-[999] grid place-items-center bg-black/30 px-4">{children}</div>;
}

/* ---------------- Template Cards ---------------- */

function DraftCard({ title, count, totalWeight, onAdd, onDelete, isLatest }) {
    return (
        <div className="relative w-11/12 shrink-0 rounded-2xl bg-white border shadow-[#00000040] shadow-md border-slate-100 px-5 py-6">
            {isLatest && (
                <div className="absolute -top-[1px] left-0">
                    <div className="rounded-br-2xl rounded-tl-2xl bg-[#F5F5F5] px-5 py-2 text-[13px] font-medium text-slate-600">
                        ล่าสุด
                    </div>
                </div>
            )}

            <button
                type="button"
                onClick={onDelete}
                className="absolute top-3 right-3 h-8 w-8 rounded-md bg-[#F7E3E3] grid place-items-center hover:bg-red-100"
                aria-label="ลบแบบร่าง"
            >
                <Trash className="w-4 h-4" />
            </button>

            <div className="text-center pt-6">
                <div className="text-[16px] font-extrabold text-[#4B5563] line-clamp-2">{title}</div>
                <div className="mt-2 text-[14px] text-[#4B5563]">{count} รายการ</div>
                {/* {totalWeight != null && (
                    <div className="mt-1 text-[13px] text-slate-500">รวม {String(totalWeight)} กก.</div>
                )} */}
            </div>

            <button
                type="button"
                onClick={onAdd}
                className="mt-6 w-full rounded-xl bg-[#3E6F5E] py-3 text-[15px] font-medium text-white hover:bg-[#215a46] active:scale-[0.99] transition"
            >
                + กดเพื่อใช้แบบร่าง
            </button>
        </div>
    );
}

function DraftEmptyCard({ loading }) {
    return (
        <div className="relative w-11/12 shrink-0 rounded-2xl bg-white shadow-[#00000040] shadow-md border border-slate-100 px-5 py-6">
            <div className="text-center">
                <div className="text-[18px] font-bold text-[#4B5563]">
                    {loading ? "กำลังโหลดแบบร่าง..." : "ยังไม่มีแบบร่าง"}
                </div>
                <div className="mt-2 text-[14px] text-[#4B5563]">
                    เพิ่มรายการด้านล่าง แล้วกด “บันทึกแบบร่าง”
                </div>
            </div>

            <div className="mt-6 w-full rounded-xl bg-[#F5F5F5] py-3 text-center text-[15px] font-medium text-[#4B5563]">
                + กดเพื่อใช้แบบร่าง
            </div>
        </div>
    );
}
